import { useState, useEffect } from "react";
import axios from "axios";
import {
  Package, DollarSign, Clock, Truck, CheckCircle, Search, Filter,
  Eye, Edit2, X, Save, RefreshCw, ChevronDown
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const STATUS_OPTIONS = ["pending", "processing", "shipped", "delivered", "cancelled"];
const STATUS_COLORS = {
  pending: "bg-gray-100 text-gray-700",
  processing: "bg-yellow-100 text-yellow-700",
  shipped: "bg-blue-100 text-blue-700",
  delivered: "bg-green-100 text-green-700",
  cancelled: "bg-red-100 text-red-700",
};
const PAYMENT_COLORS = {
  paid: "bg-green-100 text-green-700",
  pending: "bg-yellow-100 text-yellow-700",
  pending_manual: "bg-orange-100 text-orange-700",
  failed: "bg-red-100 text-red-700",
};
const METHOD_COLORS = {
  stripe: { bg: "#635BFF", label: "Stripe" },
  zelle: { bg: "#6D1ED4", label: "Zelle" },
  venmo: { bg: "#3D95CE", label: "Venmo" },
  cashapp: { bg: "#00C244", label: "Cash App" },
  bitcoin: { bg: "#F7931A", label: "Bitcoin" },
};

function StatCard({ icon, label, value, color }) {
  return (
    <div className={`bg-white rounded-2xl p-6 border border-[#E6E2D3] flex items-center gap-4`}>
      <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${color}`}>
        {icon}
      </div>
      <div>
        <p className="text-2xl font-bold text-[#1A1A1A]">{value}</p>
        <p className="text-sm text-[#5A5A5A]">{label}</p>
      </div>
    </div>
  );
}

function OrderModal({ order, token, onClose, onUpdate }) {
  const [form, setForm] = useState({
    order_status: order.order_status || "pending",
    tracking_number: order.tracking_number || "",
    notes: order.notes || "",
    customer_name: order.customer_name || "",
    customer_email: order.customer_email || "",
    customer_phone: order.customer_phone || "",
  });
  const [saving, setSaving] = useState(false);
  const [marking, setMarking] = useState(false);
  const [error, setError] = useState("");

  const methodInfo = METHOD_COLORS[order.payment_method] || { bg: "#888", label: order.payment_method };
  const isPendingManual = order.payment_status === "pending_manual";

  const handleMarkPaid = async () => {
    setMarking(true);
    setError("");
    try {
      const res = await axios.post(`${API}/admin/orders/${order.id || order._id}/mark-paid`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      onUpdate(res.data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to mark as paid");
    } finally {
      setMarking(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      const res = await axios.put(`${API}/admin/orders/${order.id || order._id}`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      onUpdate(res.data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to update order");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="order-modal">
      <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between p-6 border-b border-[#E6E2D3]">
          <h2 className="text-xl font-bold text-[#1A1A1A]" style={{ fontFamily: "'Playfair Display', serif" }}>
            Order {order.order_number}
          </h2>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-[#E6E2D3] transition-colors"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-6">
          {/* Mark as Paid Banner */}
          {isPendingManual && (
            <div className="bg-orange-50 border border-orange-200 rounded-2xl p-4 flex items-center justify-between gap-4">
              <div>
                <p className="font-bold text-orange-800 text-sm">Awaiting Manual Payment</p>
                <p className="text-orange-700 text-xs mt-0.5">
                  Customer selected <strong>{methodInfo.label}</strong>. Mark as paid once you verify the transfer.
                </p>
              </div>
              <button
                onClick={handleMarkPaid}
                disabled={marking}
                className="flex-shrink-0 flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-full text-sm font-bold hover:bg-green-700 transition-all disabled:opacity-60"
                data-testid="mark-paid-btn"
              >
                <CheckCircle className="w-4 h-4" />
                {marking ? "Marking..." : "Mark as Paid"}
              </button>
            </div>
          )}
          <div>
            <h3 className="font-bold text-[#1A1A1A] mb-3">Items Ordered</h3>
            <div className="space-y-2">
              {order.items?.map((item, i) => (
                <div key={i} className="flex justify-between items-center p-3 bg-[#FDFBF7] rounded-xl border border-[#E6E2D3]">
                  <div>
                    <p className="font-medium text-sm">{item.name}</p>
                    <p className="text-xs text-[#5A5A5A]">Type: {item.type}</p>
                  </div>
                  <p className="font-bold text-[#2F5233]">${item.price?.toFixed(2)}</p>
                </div>
              ))}
              <div className="flex justify-between p-3 font-bold text-[#1A1A1A]">
                <span>Total</span>
                <span className="text-[#2F5233]">${order.total?.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Customer Info */}
          <div>
            <h3 className="font-bold text-[#1A1A1A] mb-3">Customer Information</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-[#5A5A5A] mb-1 block">Name</label>
                <input value={form.customer_name} onChange={e => setForm(p => ({ ...p, customer_name: e.target.value }))}
                  className="w-full bg-[#F5F5F0] rounded-xl px-3 py-2 text-sm border border-transparent focus:outline-none focus:border-[#2F5233]"
                  data-testid="edit-customer-name" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#5A5A5A] mb-1 block">Email</label>
                <input value={form.customer_email} onChange={e => setForm(p => ({ ...p, customer_email: e.target.value }))}
                  className="w-full bg-[#F5F5F0] rounded-xl px-3 py-2 text-sm border border-transparent focus:outline-none focus:border-[#2F5233]"
                  data-testid="edit-customer-email" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#5A5A5A] mb-1 block">Phone</label>
                <input value={form.customer_phone} onChange={e => setForm(p => ({ ...p, customer_phone: e.target.value }))}
                  className="w-full bg-[#F5F5F0] rounded-xl px-3 py-2 text-sm border border-transparent focus:outline-none focus:border-[#2F5233]"
                  data-testid="edit-customer-phone" />
              </div>
            </div>
          </div>

          {/* Shipping */}
          {order.shipping_address && (
            <div>
              <h3 className="font-bold text-[#1A1A1A] mb-2">Shipping Address</h3>
              <p className="text-sm text-[#5A5A5A] bg-[#FDFBF7] p-3 rounded-xl border border-[#E6E2D3]">
                {order.shipping_address.street}, {order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.zip_code}, {order.shipping_address.country}
              </p>
            </div>
          )}

          {/* Status Update */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-[#5A5A5A] mb-1 block">Order Status</label>
              <select value={form.order_status} onChange={e => setForm(p => ({ ...p, order_status: e.target.value }))}
                className="w-full bg-[#F5F5F0] rounded-xl px-3 py-2 text-sm border border-transparent focus:outline-none focus:border-[#2F5233]"
                data-testid="edit-order-status">
                {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#5A5A5A] mb-1 block">Tracking Number</label>
              <input value={form.tracking_number} onChange={e => setForm(p => ({ ...p, tracking_number: e.target.value }))}
                placeholder="Enter tracking number"
                className="w-full bg-[#F5F5F0] rounded-xl px-3 py-2 text-sm border border-transparent focus:outline-none focus:border-[#2F5233]"
                data-testid="edit-tracking-number" />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-[#5A5A5A] mb-1 block">Admin Notes</label>
            <textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
              rows={3} placeholder="Internal notes about this order..."
              className="w-full bg-[#F5F5F0] rounded-xl px-3 py-2 text-sm border border-transparent focus:outline-none focus:border-[#2F5233] resize-none"
              data-testid="edit-notes" />
          </div>

          {error && <p className="text-red-500 text-sm">{error}</p>}

          <div className="flex gap-3">
            <button onClick={handleSave} disabled={saving}
              className="flex-1 flex items-center justify-center gap-2 bg-[#2F5233] text-white py-3 rounded-full font-bold hover:bg-[#1E3A21] transition-all disabled:opacity-60"
              data-testid="save-order-btn">
              <Save className="w-4 h-4" />
              {saving ? "Saving..." : "Save Changes"}
            </button>
            <button onClick={onClose} className="px-6 py-3 border border-[#E6E2D3] rounded-full text-[#5A5A5A] hover:bg-[#E6E2D3] transition-all font-medium">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { getToken } = useAuth();
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [page, setPage] = useState(0);
  const LIMIT = 20;

  const token = getToken();

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, ordersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/orders`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { status: statusFilter, search: search || undefined, skip: page * LIMIT, limit: LIMIT }
        })
      ]);
      setStats(statsRes.data);
      setOrders(ordersRes.data.orders);
      setTotal(ordersRes.data.total);
    } catch (err) {
      console.error("Admin fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [statusFilter, page]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    fetchData();
  };

  const handleOrderUpdate = (updatedOrder) => {
    setOrders(prev => prev.map(o => (o.id || o._id) === (updatedOrder.id || updatedOrder._id) ? updatedOrder : o));
    fetchData(); // refresh stats
  };

  return (
    <div className="min-h-screen py-8" style={{ backgroundColor: "#FDFBF7" }} data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-[#1A1A1A]" style={{ fontFamily: "'Playfair Display', serif" }}>Admin Dashboard</h1>
            <p className="text-[#5A5A5A] mt-1">Manage orders and track your Gift it Forward impact</p>
          </div>
          <button onClick={fetchData} className="flex items-center gap-2 p-3 rounded-full border border-[#E6E2D3] hover:bg-[#E6E2D3] transition-colors" data-testid="refresh-btn">
            <RefreshCw className={`w-4 h-4 text-[#2F5233] ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
            <StatCard icon={<Package className="w-6 h-6 text-[#2F5233]" />} label="Total Orders" value={stats.total_orders} color="bg-[#B2D3C2]" />
            <StatCard icon={<DollarSign className="w-6 h-6 text-[#D2691E]" />} label="Revenue" value={`$${stats.revenue?.toFixed(0)}`} color="bg-orange-100" />
            <StatCard icon={<Clock className="w-6 h-6 text-yellow-600" />} label="Processing" value={stats.processing} color="bg-yellow-100" />
            <StatCard icon={<Truck className="w-6 h-6 text-blue-600" />} label="Shipped" value={stats.shipped} color="bg-blue-100" />
            <StatCard icon={<CheckCircle className="w-6 h-6 text-green-600" />} label="Delivered" value={stats.delivered} color="bg-green-100" />
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-3xl border border-[#E6E2D3] p-6 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <form onSubmit={handleSearch} className="flex-1 flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5A5A5A]" />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, email, or order #"
                  className="w-full bg-[#F5F5F0] pl-9 pr-4 py-2.5 rounded-xl border border-transparent focus:outline-none focus:border-[#2F5233] text-sm"
                  data-testid="admin-search" />
              </div>
              <button type="submit" className="px-5 py-2.5 bg-[#2F5233] text-white rounded-xl font-medium text-sm hover:bg-[#1E3A21] transition-colors" data-testid="admin-search-btn">
                Search
              </button>
            </form>

            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-[#5A5A5A]" />
              <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(0); }}
                className="bg-[#F5F5F0] rounded-xl px-3 py-2.5 text-sm border border-transparent focus:outline-none focus:border-[#2F5233]"
                data-testid="admin-status-filter">
                <option value="all">All Orders</option>
                <option value="pending_manual_pay">Awaiting Manual Payment</option>
                {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* Orders Table */}
        <div className="bg-white rounded-3xl border border-[#E6E2D3] overflow-hidden" data-testid="orders-table">
          {loading ? (
            <div className="flex items-center justify-center p-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2F5233]"></div>
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center p-12 text-[#5A5A5A]">No orders found.</div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-[#FDFBF7] border-b border-[#E6E2D3]">
                      <th className="text-left px-6 py-4 text-xs font-bold text-[#5A5A5A] uppercase tracking-wide">Order #</th>
                      <th className="text-left px-6 py-4 text-xs font-bold text-[#5A5A5A] uppercase tracking-wide">Customer</th>
                      <th className="text-left px-6 py-4 text-xs font-bold text-[#5A5A5A] uppercase tracking-wide hidden md:table-cell">Items</th>
                      <th className="text-left px-6 py-4 text-xs font-bold text-[#5A5A5A] uppercase tracking-wide">Total</th>
                      <th className="text-left px-6 py-4 text-xs font-bold text-[#5A5A5A] uppercase tracking-wide">Method</th>
                      <th className="text-left px-6 py-4 text-xs font-bold text-[#5A5A5A] uppercase tracking-wide">Payment</th>
                      <th className="text-left px-6 py-4 text-xs font-bold text-[#5A5A5A] uppercase tracking-wide">Status</th>
                      <th className="text-left px-6 py-4 text-xs font-bold text-[#5A5A5A] uppercase tracking-wide hidden lg:table-cell">Date</th>
                      <th className="text-center px-6 py-4 text-xs font-bold text-[#5A5A5A] uppercase tracking-wide">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E6E2D3]">
                    {orders.map(order => (
                      <tr key={order.id || order._id} className="hover:bg-[#FDFBF7] transition-colors" data-testid={`order-row-${order.order_number}`}>
                        <td className="px-6 py-4">
                          <span className="font-mono text-sm font-semibold text-[#2F5233]">{order.order_number}</span>
                        </td>
                        <td className="px-6 py-4">
                          <p className="font-semibold text-sm text-[#1A1A1A]">{order.customer_name}</p>
                          <p className="text-xs text-[#5A5A5A]">{order.customer_email}</p>
                        </td>
                        <td className="px-6 py-4 hidden md:table-cell">
                          <p className="text-sm text-[#5A5A5A]">{order.items?.length} item(s)</p>
                        </td>
                        <td className="px-6 py-4">
                          <span className="font-bold text-[#1A1A1A]">${order.total?.toFixed(2)}</span>
                        </td>
                        <td className="px-6 py-4">
                          {(() => {
                            const m = METHOD_COLORS[order.payment_method] || { bg: "#888", label: order.payment_method || "—" };
                            return (
                              <span className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full text-white" style={{ backgroundColor: m.bg }}>
                                {m.label}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${PAYMENT_COLORS[order.payment_status] || "bg-gray-100 text-gray-600"}`}>
                            {order.payment_status === "pending_manual" ? "Awaiting Payment" : order.payment_status}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS_COLORS[order.order_status] || "bg-gray-100 text-gray-600"}`}>
                            {order.order_status}
                          </span>
                          {order.tracking_number && (
                            <p className="text-xs text-[#5A5A5A] mt-1">Track: {order.tracking_number}</p>
                          )}
                        </td>
                        <td className="px-6 py-4 hidden lg:table-cell">
                          <p className="text-xs text-[#5A5A5A]">
                            {new Date(order.created_at).toLocaleDateString()}
                          </p>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <div className="flex items-center justify-center gap-1 flex-wrap">
                            {order.payment_status === "pending_manual" && (
                              <button
                                onClick={async () => {
                                  try {
                                    const res = await axios.post(`${API}/admin/orders/${order.id || order._id}/mark-paid`, {}, { headers: { Authorization: `Bearer ${token}` } });
                                    handleOrderUpdate(res.data);
                                  } catch (e) { alert("Failed to mark as paid"); }
                                }}
                                className="inline-flex items-center gap-1 bg-green-600 text-white text-xs font-bold px-3 py-1.5 rounded-full hover:bg-green-700 transition-colors"
                                data-testid={`quick-mark-paid-${order.order_number}`}
                              >
                                <CheckCircle className="w-3.5 h-3.5" /> Mark Paid
                              </button>
                            )}
                            <button
                              onClick={() => setSelectedOrder(order)}
                              className="p-2 rounded-full hover:bg-[#E6E2D3] transition-colors inline-flex items-center gap-1 text-xs font-medium text-[#2F5233]"
                              data-testid={`edit-order-${order.order_number}`}
                            >
                              <Edit2 className="w-4 h-4" /> Edit
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {total > LIMIT && (
                <div className="flex items-center justify-between px-6 py-4 border-t border-[#E6E2D3]">
                  <p className="text-sm text-[#5A5A5A]">Showing {page * LIMIT + 1}–{Math.min((page + 1) * LIMIT, total)} of {total}</p>
                  <div className="flex gap-2">
                    <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
                      className="px-4 py-2 border border-[#E6E2D3] rounded-xl text-sm font-medium disabled:opacity-40 hover:bg-[#E6E2D3] transition-colors">
                      Previous
                    </button>
                    <button onClick={() => setPage(p => p + 1)} disabled={(page + 1) * LIMIT >= total}
                      className="px-4 py-2 border border-[#E6E2D3] rounded-xl text-sm font-medium disabled:opacity-40 hover:bg-[#E6E2D3] transition-colors">
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Order Edit Modal */}
      {selectedOrder && (
        <OrderModal
          order={selectedOrder}
          token={token}
          onClose={() => setSelectedOrder(null)}
          onUpdate={handleOrderUpdate}
        />
      )}
    </div>
  );
}
